import {
  PROTOCOL_VERSION,
  type ErrorCode,
  type Presenter,
  type RoomView,
  type ServerMessage,
} from '../shared/protocol.js';
import { Connection, type ConnectionStatus } from './lib/connection.js';

interface ScreenSession {
  code: string;
  screenToken: string;
}

const SESSION_KEY = 'clicker.screen.v1';

const ERROR_TEXT: Record<ErrorCode, string> = {
  room_not_found: '房间不存在或已过期',
  invalid_token: '大屏凭证已失效',
  room_full: '房间人数已满',
  room_locked: '房间已锁定',
  not_owner: '只有主持人可以操作',
  presenter_not_found: '找不到这个投屏人',
  frame_too_large: '这一页太复杂，已降低清晰度或改用上传模式',
  rate_limited: '操作太频繁，请稍后再试',
  too_many_attempts: '尝试次数过多，请稍后再试',
  bad_message: '数据格式错误',
  server_busy: '服务器繁忙，请稍后再试',
  room_expired: '房间已过期',
  unsupported_version: '版本不兼容，请刷新页面',
  internal: '服务器内部错误',
  viewer_name_taken: '该昵称已被占用',
};

function must<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`missing element ${selector}`);
  return el;
}

function loadSession(): ScreenSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScreenSession>;
    if (
      typeof parsed.code === 'string' &&
      /^\d{6}$/.test(parsed.code) &&
      typeof parsed.screenToken === 'string' &&
      parsed.screenToken.length >= 16
    ) {
      return { code: parsed.code, screenToken: parsed.screenToken };
    }
    return null;
  } catch {
    return null;
  }
}

function saveSession(session: ScreenSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // private mode: screen simply re-creates a room on reload
  }
}

function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

interface PaneView {
  root: HTMLElement;
  image: HTMLImageElement;
  label: HTMLElement;
  laser: HTMLElement;
  loading: HTMLElement;
  src: string;
  jpegTried: boolean;
}

const panesEl = must<HTMLElement>('#panes');
const idleEl = must<HTMLElement>('#idle');
const idleCode = must<HTMLElement>('#idle-code');
const idleTitle = must<HTMLElement>('#idle-title');
const idleSub = must<HTMLElement>('#idle-sub');
const idleHint = must<HTMLElement>('#idle-hint');
const idleQr = must<HTMLImageElement>('#idle-qr');
const connChip = must<HTMLElement>('#conn-chip');
const remotesChip = must<HTMLElement>('#remotes-chip');
const deckChip = must<HTMLElement>('#deck-chip');
const pageHud = must<HTMLElement>('#page-hud');
const pairPanel = must<HTMLElement>('#pair-panel');
const pairCode = must<HTMLElement>('#pair-code');
const pairQr = must<HTMLImageElement>('#pair-qr');
const pairUrl = must<HTMLElement>('#pair-url');
const pairLive = must<HTMLElement>('#pair-live');
const pairToggle = must<HTMLButtonElement>('#pair-toggle');
const fullscreenButton = must<HTMLButtonElement>('#fullscreen');
const resetRoomButton = must<HTMLButtonElement>('#reset-room');
const copyButton = must<HTMLButtonElement>('#copy-link');
const toastEl = must<HTMLElement>('#toast');
const inviteOverlay = must<HTMLElement>('#invite-overlay');
const inviteCode = must<HTMLElement>('#invite-code');
const inviteQr = must<HTMLImageElement>('#invite-qr');
const inviteUrl = must<HTMLElement>('#invite-url');

let code = '';
let screenToken = '';
let room: RoomView | null = null;
let calibration: number[] | null = null;
let forceCreate = false;
let pairPinned = false;
let toastTimer = 0;
let laserTimer = 0;
let laserPaneId: string | null = null;

const panes = new Map<string, PaneView>();
const placeholders = new Map<number, HTMLElement>();
interface LiveFrame {
  page: number;
  total: number;
  image: string;
  width: number;
  height: number;
  capture?: boolean;
}
const liveFrames = new Map<string, LiveFrame>();

const conn = new Connection({
  onMessage: handleMessage,
  onStatus: handleStatus,
  onOpen: handleOpen,
});

function handleOpen(): void {
  const saved = loadSession();
  if (forceCreate || !saved) {
    conn.send({ v: PROTOCOL_VERSION, type: 'create', clientId: CLIENT_ID });
    return;
  }
  screenToken = saved.screenToken;
  conn.send({
    v: PROTOCOL_VERSION,
    type: 'join',
    code: saved.code,
    role: 'screen',
    clientId: CLIENT_ID,
    screenToken: saved.screenToken,
  });
}

function handleMessage(message: ServerMessage): void {
  switch (message.type) {
    case 'created':
      code = message.code;
      screenToken = message.screenToken;
      saveSession({ code, screenToken });
      forceCreate = false;
      setPairingCode(message.code);
      applyRoom(message.room);
      break;

    case 'joined':
      code = message.code;
      setPairingCode(message.code);
      applyRoom(message.room);
      break;

    case 'room-state':
      applyRoom(message.room);
      break;

    case 'laser':
      showLaser(message.presenterId, message.x, message.y, message.active);
      break;

    case 'frame':
      handleFrame(message);
      break;

    case 'calibration-result':
      calibration = message.matrix;
      showToast('激光校准已生效', 2400);
      break;

    case 'host-command':
      if (message.action === 'toggle-hud') {
        document.body.classList.toggle('is-presenting');
        showToast(
          document.body.classList.contains('is-presenting') ? '已进入演示模式' : '已退出演示模式',
          2000,
        );
      } else if (message.action === 'toggle-fullscreen') {
        void toggleFullscreen();
      }
      break;

    case 'error':
      handleError(message.code, message.message, message.fatal === true);
      break;

    case 'shutdown':
      showToast('服务器正在重启，几秒后自动重连…', 4000);
      break;

    case 'host-changed':
    case 'kicked':
    case 'viewer-question':
    case 'viewer-joined':
    case 'viewer-left':
    case 'presenter-state':
    case 'pong':
    case 'sync-response':
      break;
  }
}

function handleError(errorCode: ErrorCode, fallback: string, fatal: boolean): void {
  const text = ERROR_TEXT[errorCode] ?? fallback;
  if (!fatal) {
    showToast(text, 3000);
    return;
  }
  const saved = loadSession();
  if (saved && (errorCode === 'room_not_found' || errorCode === 'invalid_token')) {
    clearSession();
    forceCreate = true;
    showToast('原房间已失效，正在创建新房间…', 3000);
    return;
  }
  conn.stop();
  showToast(`${text}，请刷新页面重试`, 6000);
}

function handleStatus(status: ConnectionStatus): void {
  connChip.dataset.status = status;
  switch (status) {
    case 'connecting':
      connChip.textContent = '连接中';
      break;
    case 'open':
      connChip.textContent = '已连接';
      break;
    case 'reconnecting':
      connChip.textContent = '重连中';
      break;
    case 'closed':
      connChip.textContent = '已断开';
      break;
  }
}

function setPairingCode(next: string): void {
  pairCode.textContent = next;
  idleCode.textContent = next;
  pairQr.src = `/api/qr.svg?code=${encodeURIComponent(next)}`;
  pairQr.alt = `投屏码二维码，投屏码 ${next}`;
  pairQr.hidden = false;
  idleQr.src = `/api/qr.svg?code=${encodeURIComponent(next)}`;
  idleQr.alt = `投屏码二维码，投屏码 ${next}`;
  idleQr.hidden = false;
  pairUrl.textContent = window.location.host;
}

function activePresenters(): Presenter[] {
  if (!room) return [];
  return room.activeIds
    .map((id) => room!.presenters.find((presenter) => presenter.id === id))
    .filter((presenter): presenter is Presenter => Boolean(presenter));
}

function applyRoom(next: RoomView): void {
  const previousOwner = room?.ownerName ?? null;
  room = next;
  for (const presenter of next.presenters) {
    if (!presenter.live) liveFrames.delete(presenter.id);
  }
  render();

  if (next.ownerName && !previousOwner) {
    showToast(`${next.ownerName} 接管了屏幕`, 2600);
  }
}

function render(): void {
  if (!room) return;
  renderInvite();
  const active = activePresenters();

  idleEl.hidden = active.length > 0;
  panesEl.hidden = active.length === 0;
  if (active.length === 0) {
    if (room.ownerName) {
      idleTitle.textContent = '等待投屏';
      idleSub.textContent = room.ownerOnline
        ? `主持人 ${room.ownerName} 正在控制这块屏`
        : `主持人 ${room.ownerName} 已离线 · 30 分钟后可重新接管`;
      idleHint.textContent = room.ownerOnline
        ? '其他人扫码后可上传文件，并申请上屏'
        : '也可以点右上角「重置」立即重新开始';
    } else {
      idleTitle.textContent = '把 PPT 投到这块屏';
      idleSub.textContent = '手机扫码 · 笔记本投屏 · 多人轮流';
      idleHint.textContent = '第一个扫码的人是主持人';
    }
  }
  panesEl.dataset.layout = String(room.layout);
  panesEl.dataset.count = String(room.layout);

  syncPanes(active);
  renderHud(active);
  renderPairPanel(active);
}

function syncPanes(active: Presenter[]): void {
  if (active.length === 0) {
    for (const pane of panes.values()) pane.root.remove();
    panes.clear();
    for (const placeholder of placeholders.values()) placeholder.remove();
    placeholders.clear();
    return;
  }
  const keep = new Set(active.map((presenter) => presenter.id));
  for (const [id, pane] of panes) {
    if (!keep.has(id)) {
      pane.root.remove();
      panes.delete(id);
    }
  }

  const slots = room?.layout ?? 1;
  for (const [slot, element] of placeholders) {
    if (slot >= slots) {
      element.remove();
      placeholders.delete(slot);
    }
  }

  for (let slot = 0; slot < slots; slot += 1) {
    const presenter = active[slot];
    if (presenter) {
      const stale = placeholders.get(slot);
      if (stale) {
        stale.remove();
        placeholders.delete(slot);
      }
      let pane = panes.get(presenter.id);
      if (!pane) {
        pane = createPane(presenter.id);
        panes.set(presenter.id, pane);
      }
      updatePane(pane, presenter);
      panesEl.appendChild(pane.root);
      continue;
    }

    let placeholder = placeholders.get(slot);
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'pane pane--empty';
      const label = document.createElement('span');
      label.textContent = `空位 ${slot + 1} · 等待投屏人`;
      placeholder.appendChild(label);
      placeholders.set(slot, placeholder);
    }
    panesEl.appendChild(placeholder);
  }
}

function createPane(presenterId: string): PaneView {
  const root = document.createElement('figure');
  root.className = 'pane';
  root.dataset.presenterId = presenterId;

  const loading = document.createElement('div');
  loading.className = 'pane__loading';
  loading.textContent = '等待画面…';

  const image = document.createElement('img');
  image.className = 'pane__image';
  image.alt = '';
  image.decoding = 'async';

  const label = document.createElement('figcaption');
  label.className = 'pane__label';

  const laser = document.createElement('div');
  laser.className = 'pane__laser';

  root.append(image, loading, label, laser);
  return { root, image, label, laser, loading, src: '', jpegTried: false };
}

function updatePane(pane: PaneView, presenter: Presenter): void {
  const owner = presenter.owner ? ' · 主持人' : '';
  pane.label.textContent = presenter.deck
    ? `${presenter.name}${owner} · ${presenter.page}/${presenter.total}`
    : `${presenter.name}${owner} · 未上传`;
  pane.label.title = presenter.deck?.name ?? '';

  const live = liveFrames.get(presenter.id);
  if (presenter.live && live) {
    if (pane.src !== live.image) {
      pane.src = live.image;
      pane.image.src = live.image;
      pane.loading.hidden = true;
    }
    const mode = live.capture ? '共享窗口' : `${live.page}/${live.total}`;
    pane.label.textContent = `${presenter.name}${presenter.owner && presenter.name !== '主持人' ? ' · 主持人' : ''} · ${mode}`;
    return;
  }

  if (!presenter.deck) {
    pane.image.removeAttribute('src');
    pane.src = '';
    pane.loading.hidden = false;
    pane.loading.textContent = '还没有上传内容';
    return;
  }

  const src = `/api/decks/${presenter.deck.id}/pages/${presenter.page}.webp`;
  if (pane.src === src) return;
  pane.src = src;
  pane.jpegTried = false;
  pane.loading.hidden = false;
  pane.loading.textContent = '正在渲染…';

  const request = src;
  const probe = new Image();
  probe.decoding = 'async';
  probe.onload = () => {
    if (pane.src !== request) return;
    pane.image.src = src;
    pane.loading.hidden = true;
  };
  probe.onerror = () => {
    if (pane.src !== request) return;
    if (!pane.jpegTried) {
      pane.jpegTried = true;
      const fallback = `/api/decks/${presenter.deck!.id}/pages/${presenter.page}.jpg`;
      const retry = new Image();
      retry.onload = () => {
        if (pane.src !== request) return;
        pane.image.src = fallback;
        pane.loading.hidden = true;
      };
      retry.onerror = () => {
        if (pane.src !== request) return;
        pane.loading.hidden = false;
        pane.loading.textContent = '这一页加载失败';
      };
      retry.src = fallback;
      return;
    }
    pane.loading.hidden = false;
    pane.loading.textContent = '这一页加载失败';
  };
  probe.src = src;
}

function renderHud(active: Presenter[]): void {
  if (!room) return;
  const withDeck = active.find((presenter) => presenter.deck || presenter.live);
  deckChip.hidden = !withDeck;
  if (withDeck) {
    deckChip.textContent =
      withDeck.deck?.name ?? (withDeck.capture ? `${withDeck.name} · 共享窗口` : `${withDeck.name} · 本机`);
    deckChip.title = withDeck.deck?.name ?? withDeck.name;
  }
  pageHud.textContent = withDeck
    ? withDeck.capture
      ? '共享窗口'
      : `${withDeck.page} / ${withDeck.total}`
    : '— / —';

  const presenters = room.presenters.length;
  remotesChip.textContent =
    presenters === 0 ? '等待投屏' : `${presenters} 位投屏人 · ${active.length} 个上屏`;
  remotesChip.dataset.active = active.length > 0 ? 'true' : 'false';
}

function renderPairPanel(active: Presenter[]): void {
  if (!room) return;
  pairLive.textContent = room.ownerName
    ? `主持人 ${room.ownerName}${room.ownerOnline ? '' : '（离线）'}`
    : '等待接管';

  if (!pairPinned) pairPanel.classList.add('is-hidden');
}

function handleFrame(message: Extract<ServerMessage, { type: 'frame' }>): void {
  liveFrames.set(message.presenterId, {
    page: message.page,
    total: message.total,
    image: message.image,
    width: message.width,
    height: message.height,
    capture: message.capture === true,
  });
  if (room) {
    const presenter = room.presenters.find((entry) => entry.id === message.presenterId);
    if (presenter) {
      presenter.page = message.page;
      presenter.total = message.total;
      presenter.live = true;
      presenter.capture = message.capture === true;
      const pane = panes.get(presenter.id);
      if (pane) updatePane(pane, presenter);
    }
    renderHud(activePresenters());
  }
}

function showLaser(presenterId: string, x: number, y: number, active: boolean): void {
  const pane = panes.get(presenterId);
  if (!pane) return;
  if (laserPaneId && laserPaneId !== presenterId) {
    panes.get(laserPaneId)?.laser.classList.remove('is-on');
  }
  laserPaneId = presenterId;
  if (!active) {
    pane.laser.classList.remove('is-on');
    return;
  }
  const point = calibration ? applyHomography(calibration, x, y) : { x, y };
  pane.laser.style.left = `${Math.min(Math.max(point.x, 0), 1) * 100}%`;
  pane.laser.style.top = `${Math.min(Math.max(point.y, 0), 1) * 100}%`;
  pane.laser.classList.add('is-on');
  if (laserTimer) window.clearTimeout(laserTimer);
  laserTimer = window.setTimeout(() => pane.laser.classList.remove('is-on'), 1500);
}

function applyHomography(matrix: number[], x: number, y: number): { x: number; y: number } {
  const w = matrix[6]! * x + matrix[7]! * y + matrix[8]!;
  if (!Number.isFinite(w) || Math.abs(w) < 1e-9) return { x, y };
  const tx = (matrix[0]! * x + matrix[1]! * y + matrix[2]!) / w;
  const ty = (matrix[3]! * x + matrix[4]! * y + matrix[5]!) / w;
  if (!Number.isFinite(tx) || !Number.isFinite(ty)) return { x, y };
  return { x: Math.min(Math.max(tx, 0), 1), y: Math.min(Math.max(ty, 0), 1) };
}

function showToast(text: string, duration = 2600): void {
  toastEl.textContent = text;
  toastEl.hidden = false;
  toastEl.classList.add('is-visible');
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastEl.classList.remove('is-visible');
    window.setTimeout(() => {
      toastEl.hidden = true;
    }, 220);
  }, duration);
}

function renderInvite(): void {
  if (!room || !room.inviteVisible) {
    inviteOverlay.hidden = true;
    return;
  }
  const currentCode = code || room.code;
  inviteCode.textContent = currentCode;
  inviteQr.src = `/api/qr.svg?code=${encodeURIComponent(currentCode)}`;
  inviteUrl.textContent = `${window.location.origin}/remote.html`;
  inviteOverlay.hidden = false;
}

function togglePairPanel(): void {
  const willShow = pairPanel.classList.contains('is-hidden');
  pairPinned = willShow;
  pairPanel.classList.toggle('is-hidden', !willShow);
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    showToast('浏览器不允许全屏，请按 F11', 3000);
  }
}

async function copyInvite(): Promise<void> {
  if (!code) return;
  const url = `${window.location.origin}/remote.html?code=${code}`;
  try {
    await navigator.clipboard.writeText(url);
    showToast('邀请链接已复制', 2200);
  } catch {
    showToast(url, 5000);
  }
}

const CLIENT_ID = (() => {
  const key = 'clicker.client.v1';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
})();

window.addEventListener('keydown', (event) => {
  if (event.key === 'f' || event.key === 'F') {
    event.preventDefault();
    void toggleFullscreen();
  } else if (event.key === 'c' || event.key === 'C') {
    event.preventDefault();
    togglePairPanel();
  } else if (event.key === 'h' || event.key === 'H' || event.key === 'Escape') {
    event.preventDefault();
    document.body.classList.remove('is-presenting');
    showToast('已显示控制栏', 1800);
  }
});

pairToggle.addEventListener('click', togglePairPanel);
fullscreenButton.addEventListener('click', () => void toggleFullscreen());
resetRoomButton.addEventListener('click', () => {
  if (!window.confirm('重置房间会踢出所有人并清空内容，确定？')) return;
  conn.send({ v: PROTOCOL_VERSION, type: 'reset-room' });
});
copyButton.addEventListener('click', () => void copyInvite());
document.addEventListener('fullscreenchange', () => {
  fullscreenButton.textContent = document.fullscreenElement ? '退出全屏' : '全屏';
});

render();
conn.start();
